import { createFileRoute } from "@tanstack/react-router"
import { BellRing, Pencil, PiggyBank, Plus, Trash2 } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import type { BudgetPolicy } from "@/api/generated/BudgetPolicy"
import {
  useBudgetAlerts,
  useBudgetStatus,
  useBudgets,
  useDeleteBudget,
  useUpdateBudget,
} from "@/api/queries/agents"
import { useConfirm } from "@/components/app/confirm-dialog"
import { createColumns, DataTable } from "@/components/app/data-table"
import { PageContainer, PageHeader, PageSection } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { EmptyState, ErrorState } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { formatCost } from "@/features/runs/format"
import { BudgetDialog } from "@/features/studio/budget-dialog"
import { BudgetStatusCard } from "@/features/studio/budget-status"

export const Route = createFileRoute("/_app/studio/budgets")({
  staticData: { titleKey: "studio.nav.budgets" },
  component: BudgetsPage,
})

function BudgetsPage() {
  const { t } = useTranslation()
  const budgets = useBudgets()
  const status = useBudgetStatus()
  const alerts = useBudgetAlerts()
  const toggle = useUpdateBudget().mutate
  const remove = useDeleteBudget()
  const confirm = useConfirm()
  const [dialog, setDialog] = useState<{ open: boolean; policy?: BudgetPolicy }>({ open: false })

  const thresholdOf = (id: number) => budgets.data?.find((b) => b.id === id)?.alert_threshold_pct

  const columns = useMemo(
    () =>
      createColumns<BudgetPolicy>((c) => [
        c.accessor("name", {
          header: t("studio.budgets.columns.policy"),
          enableHiding: false,
          cell: ({ row }) => (
            <div className="min-w-0">
              <div className="truncate font-medium">
                {row.original.name || t("studio.budgets.unnamed", { id: row.original.id })}
              </div>
              <div className="text-muted-foreground truncate text-xs">
                {row.original.scope === "agent"
                  ? (row.original.agent_name ?? t("studio.budgets.scope.agent"))
                  : t("studio.budgets.scope.tenant")}
              </div>
            </div>
          ),
        }),
        c.accessor("period", {
          header: t("studio.budgets.columns.period"),
          cell: ({ getValue }) =>
            t(`studio.budgets.period.${getValue()}` as "studio.budgets.period.month", getValue()),
        }),
        c.accessor("max_spend", {
          header: t("studio.budgets.columns.limit"),
          meta: { align: "end" },
          cell: ({ getValue }) => <span className="tabular-nums">{formatCost(getValue())}</span>,
        }),
        c.accessor("alert_threshold_pct", {
          header: t("studio.budgets.columns.threshold"),
          meta: { align: "end" },
          cell: ({ getValue }) => <span className="text-muted-foreground tabular-nums">{getValue()}%</span>,
        }),
        c.accessor("action", {
          header: t("studio.budgets.columns.action"),
          cell: ({ getValue }) => (
            <StatusBadge tone={getValue() === "block" ? "danger" : "warning"}>
              {t(`studio.budgets.action.${getValue()}` as "studio.budgets.action.block", getValue())}
            </StatusBadge>
          ),
        }),
        c.accessor("enabled", {
          header: t("studio.budgets.columns.enabled"),
          meta: { align: "end" },
          cell: ({ row }) => (
            <div
              className="flex justify-end"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Switch
                size="sm"
                checked={row.original.enabled}
                onCheckedChange={(v) => toggle({ id: row.original.id, enabled: v })}
                aria-label={t("studio.budgets.columns.enabled")}
              />
            </div>
          ),
        }),
      ]),
    [t, toggle],
  )

  return (
    <PageContainer width="wide">
      <PageHeader
        title={t("studio.budgets.title")}
        description={t("studio.budgets.description")}
        icon={PiggyBank}
        actions={
          <Button onClick={() => setDialog({ open: true })} data-testid="new-budget">
            <Plus />
            {t("studio.budgets.new")}
          </Button>
        }
      />
      <div className="space-y-10">
        <PageSection
          title={t("studio.budgets.statusTitle")}
          description={t("studio.budgets.statusDescription")}
        >
          {status.isPending ? (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-32 rounded-xl" />
              ))}
            </div>
          ) : status.isError ? (
            <ErrorState error={status.error} onRetry={() => void status.refetch()} />
          ) : status.data.length === 0 ? (
            <EmptyState
              icon={PiggyBank}
              title={t("studio.budgets.empty.title")}
              description={t("studio.budgets.empty.description")}
              action={
                <Button size="sm" onClick={() => setDialog({ open: true })}>
                  <Plus />
                  {t("studio.budgets.new")}
                </Button>
              }
            />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {status.data.map((s) => (
                <BudgetStatusCard key={s.policy_id} status={s} threshold={thresholdOf(s.policy_id)} />
              ))}
            </div>
          )}
        </PageSection>

        {(budgets.data?.length ?? 0) > 0 && (
          <PageSection title={t("studio.budgets.policiesTitle")}>
            <DataTable
              label={t("studio.budgets.policiesTitle")}
              data={budgets.data}
              columns={columns}
              getRowId={(b) => String(b.id)}
              loading={budgets.isPending}
              error={budgets.error}
              onRetry={() => void budgets.refetch()}
              pageSize={false}
              onRowClick={(b) => setDialog({ open: true, policy: b })}
              rowActions={(b) => (
                <>
                  <DropdownMenuItem onClick={() => setDialog({ open: true, policy: b })}>
                    <Pencil />
                    {t("common.edit")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    variant="destructive"
                    onClick={() =>
                      void confirm({
                        title: t("studio.budgets.deleteTitle"),
                        description: t("studio.budgets.deleteDescription"),
                        confirmLabel: t("studio.budgets.delete"),
                        destructive: true,
                        onConfirm: () => remove.mutateAsync(b),
                      })
                    }
                  >
                    <Trash2 />
                    {t("studio.budgets.delete")}
                  </DropdownMenuItem>
                </>
              )}
            />
          </PageSection>
        )}

        <PageSection
          title={t("studio.budgets.alertsTitle")}
          description={t("studio.budgets.alertsDescription")}
        >
          {alerts.isPending ? (
            <Skeleton className="h-24 rounded-xl" />
          ) : alerts.isError ? (
            <ErrorState error={alerts.error} onRetry={() => void alerts.refetch()} />
          ) : alerts.data.length === 0 ? (
            <EmptyState size="sm" icon={BellRing} title={t("studio.budgets.noAlerts")} />
          ) : (
            <ul className="bg-card divide-y rounded-xl border">
              {alerts.data.slice(0, 50).map((a) => (
                <li key={a.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                  <BellRing className="text-warning size-4 shrink-0" />
                  <span className="min-w-0 flex-1 truncate font-medium">{a.policy_name}</span>
                  <span className="tabular-nums">{formatCost(a.current_spend)}</span>
                  <RelativeTime
                    value={a.triggered_at}
                    className="text-muted-foreground w-28 text-right text-xs"
                  />
                </li>
              ))}
            </ul>
          )}
        </PageSection>
      </div>
      <BudgetDialog
        open={dialog.open}
        policy={dialog.policy}
        onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
      />
    </PageContainer>
  )
}
